import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const INTERESTS = [
  "Dinosaurs", "Space", "Sports", "Animals", "Art", "Music",
  "Cars", "Trains", "Nature", "Robots", "Cooking", "Building",
];

const READING_LEVELS = [
  { value: "K", label: "Kindergarten" },
  { value: "grade_1", label: "Grade 1" },
  { value: "grade_2", label: "Grade 2" },
  { value: "grade_3", label: "Grade 3" },
  { value: "grade_4", label: "Grade 4" },
  { value: "grade_5", label: "Grade 5" },
  { value: "grade_6", label: "Grade 6+" },
];

export default function Onboarding() {
  const { user, giveConsent } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(user?.consent_given ? 2 : 1);
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [age, setAge] = useState(8);
  const [readingLevel, setReadingLevel] = useState("grade_2");
  const [selectedInterests, setSelectedInterests] = useState([]);

  const toggleInterest = (i) =>
    setSelectedInterests((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]));

  const submitConsent = async () => {
    if (!consent1 || !consent2) {
      toast.error("Please read and check both boxes to continue.");
      return;
    }
    setBusy(true);
    try {
      await giveConsent();
      setStep(2);
    } catch (e) {
      toast.error("Could not save consent. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const createStudent = async () => {
    if (!name.trim()) {
      toast.error("Please enter the student's name.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/students", {
        name: name.trim(),
        age: Number(age),
        reading_level: readingLevel,
        interests: selectedInterests,
        avatar_color: "#2B7A78",
      });
      toast.success(`${data.name} was added.`);
      nav(`/students/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create student.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen py-10 px-4" style={{ background: "var(--tm-bg-2)" }} data-testid="onboarding-page">
      <div className="mx-auto max-w-2xl">
        <div className="tm-card">
          <div className="flex items-center gap-3 mb-4">
            <span className="tm-chip">Step {step} of 2</span>
          </div>
          {step === 1 ? (
            <>
              <h1 className="text-3xl font-black mb-3" style={{ fontFamily: "Nunito, sans-serif" }}>
                A quick consent step
              </h1>
              <p className="text-[color:var(--tm-ink-3)] mb-6">
                ThinkMyWay is designed with autistic children in mind. Because your child
                may use this app, we need your permission before continuing.
              </p>
              <div className="tm-card-soft mb-4">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck size={22} style={{ color: "var(--tm-brand)" }} />
                  <div>
                    <p className="font-bold mb-1">Your child's data is private</p>
                    <p className="text-[color:var(--tm-ink-3)]">
                      IEP files are encrypted with AES-256 before storage. Only you can
                      view or delete them.
                    </p>
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-3 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent1}
                  onChange={(e) => setConsent1(e.target.checked)}
                  className="mt-1 w-5 h-5"
                  data-testid="onboarding-consent-1"
                />
                <span className="text-[color:var(--tm-ink-2)]">
                  I am the parent, guardian, or teacher of the child using this app.
                </span>
              </label>
              <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent2}
                  onChange={(e) => setConsent2(e.target.checked)}
                  className="mt-1 w-5 h-5"
                  data-testid="onboarding-consent-2"
                />
                <span className="text-[color:var(--tm-ink-2)]">
                  I understand that AI responses are guidance, not medical or educational
                  advice, and I will supervise my child's use of the app.
                </span>
              </label>
              <button
                onClick={submitConsent}
                disabled={busy || !consent1 || !consent2}
                className="tm-btn tm-btn-primary w-full"
                data-testid="onboarding-consent-continue"
              >
                {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                Continue
              </button>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-black mb-3" style={{ fontFamily: "Nunito, sans-serif" }}>
                Add your first student
              </h1>
              <p className="text-[color:var(--tm-ink-3)] mb-6">
                Tell us a little about them. You can upload an IEP file next.
              </p>
              <div className="space-y-5">
                <div>
                  <label htmlFor="s-name" className="tm-label">Student's first name</label>
                  <input
                    id="s-name"
                    className="tm-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Sam"
                    data-testid="onboarding-student-name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="s-age" className="tm-label">Age</label>
                    <input
                      id="s-age"
                      type="number"
                      min={5}
                      max={14}
                      className="tm-input"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      data-testid="onboarding-student-age"
                    />
                  </div>
                  <div>
                    <label htmlFor="s-read" className="tm-label">Reading level</label>
                    <select
                      id="s-read"
                      className="tm-input"
                      value={readingLevel}
                      onChange={(e) => setReadingLevel(e.target.value)}
                      data-testid="onboarding-student-reading"
                    >
                      {READING_LEVELS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <span className="tm-label">Interests (choose any that fit)</span>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => {
                      const active = selectedInterests.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleInterest(i)}
                          className={`tm-btn ${active ? "tm-btn-primary" : "tm-btn-outline"}`}
                          style={{ minHeight: 40, padding: "0.4rem 1rem" }}
                          data-testid={`onboarding-interest-${i.toLowerCase()}`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={createStudent}
                  disabled={busy}
                  className="tm-btn tm-btn-primary w-full"
                  data-testid="onboarding-create-student"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : null}
                  Add student
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
